import type { ISupportedVariantList } from '../../../constants/supportedVariants';
import { AssetSource, JobStatus, Permission } from '../../../enums';
import type { Action, Item } from '../../../models';
import type { ItemVariantStatusInfo, VariantStatus } from '../../../models/item';
import type { OU } from '../../../models/Ou';
import type { Permissions } from '../../../models/Permissions';
import { SchemaType } from '../../../store/forms/Asset-Editor/SchemaTypeOptions';
import clientUtils from '../../../utils/clientUtils';
import itemUtils from '../../../utils/itemUtils';

const STALLED_TIMEOUT_IN_HOURS = 12;

const resolveDurationString = (item: Item) => {
  if (item.duration) {
    return `${item.duration}`;
  }
  if (item.data && item.data.technicalMetadata && item.data.technicalMetadata.meta && item.data.technicalMetadata.meta.duration) {
    return item.data.technicalMetadata.meta.duration;
  }
  return '';
};

const getUploadExternalCCIconClassName = (item: Item, flags) => {
  const isClipGroup = item.source === AssetSource.ClipGroup;
  const isNestedChannel = item.source && item.source.toString() === 'NestedChannel';
  let className = 'action';
  if (item && !item.hasClosedCaption) {
    className = `${className} btn-opacity`;
  }
  if (item.schemaType === SchemaType.Graphics || item.schemaType === SchemaType.Image || isClipGroup || isNestedChannel) {
    className = `action disabled-icon`;
  }
  return className;
};

const resolveIsSelectable = (item: Item) => {
  const status = item.data.ingestStatus;
  return !(status === JobStatus.Started || status === JobStatus.Unknown);
};

const getActionClassName = (item: Item, flags, type) => {
  const isClipGroup = item.source === AssetSource.ClipGroup;
  const isNestedChannel = item.source && item.source.toString() === 'NestedChannel';
  const clippingEnabledForClipGroup = flags['fuel-ui-enabled-clipping-for-clip-group'];
  const className = 'action';
  switch (type) {
    case 'uploadExternalWebvttFile':
      return getUploadExternalCCIconClassName(item, flags);
    case 'adInsertion': {
      if (item.schemaType === SchemaType.Graphics || item.schemaType === SchemaType.Image || isClipGroup || isNestedChannel) {
        return `action disabled-icon`;
      }
      if (item.sourceFileUrl) return className;
      if (item.hlsFileUrl && !item.sourceFileUrl) {
        return className;
      }
      return `action disabled-icon`;
    }
    case 'clip': {
      if (item.schemaType === SchemaType.Graphics || item.schemaType === SchemaType.Image || isNestedChannel) {
        return `action disabled-icon`;
      }
      if (isClipGroup && !clippingEnabledForClipGroup) {
        return `action disabled-icon`;
      }
      if (item.sourceFileUrl) return className;
      if (item.hlsFileUrl && !item.sourceFileUrl) {
        return className;
      }
      return `action disabled-icon`;
    }
    case 'link': {
      if (item.schemaType === SchemaType.Graphics || item.schemaType === SchemaType.Image || isNestedChannel) {
        return 'action disabled-icon';
      }
      if (item.data.ingestStatus === JobStatus.Completed && item.seClipId) {
        return 'action';
      }

      return 'action disabled-icon';
    }
    case 'deleteAsset': {
      const isIngestComplete = IsIngestComplete(item.data.ingestStatus);
      if (isIngestComplete) {
        return className;
      }
      return 'menu-item-disabled';
    }
    default:
      return 'action';
  }
};

export const IsIngestComplete = (status): boolean => {
  switch (status) {
    case JobStatus.Unknown:
    case JobStatus.Started:
      return false;
    default:
      return true;
  }
};

const assetDisplayStatus = (item: Item): boolean => {
  if (item && item.data && item.data.assetDisplayStatus === 'Warning') {
    return true;
  }
  return false;
};

// just following the original strategy i found in advancedsearch - in-place mutations, can change if so needed later...
export const enhanceFetchedAssets: (
  items: Array<Item>,
  permissions: Permissions,
  flags: any,
  appliedExpiredOrEmbargoCss?: boolean,
  channelVariants?: string[],
  supportedVariants?: ISupportedVariantList[],
  ouData?: OU | null,
  thumbnails?: Item[]
) => Array<Item> = (
  items,
  permissions,
  flags,
  appliedExpiredOrEmbargoCss = false,
  channelVariants = [],
  supportedVariants = [],
  ouData = null,
  thumbnails = []
) => {
  if (ouData == null) {
    console.error('enhanceFetchedAssets called without ouData', { items });
  }

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    enhanceFetchedAsset(
      item,
      permissions,
      flags,
      appliedExpiredOrEmbargoCss,
      channelVariants,
      supportedVariants,
      ouData,
      thumbnails
    );
  }
  return items;
};

export const enhanceFetchedAsset: (
  item: Item,
  permissions: Permissions,
  flags: any,
  appliedExpiredOrEmbargoCss?: boolean,
  channelVariants?: string[],
  supportedVariants?: ISupportedVariantList[],
  ouData?: OU | null,
  thumbnails?: Item[]
) => Item = (
  item: Item,
  permissions,
  flags,
  appliedExpiredOrEmbargoCss = false,
  channelVariants = [],
  supportedVariants = [],
  ouData = null,
  thumbnails = []
) => {
  if (ouData == null) {
    console.error('enhanceFetchedAsset called without ouData', { item });
  }

  // console.log('enhanceFetchedAsset', item.id, item.title);
  // console.log('enhanceFetchedAsset', { item, permissions, flags, appliedExpiredOrEmbargoCss });
  const isSuperAdmin = permissions && permissions.has(Permission.OUSuperAdmin);
  const isClipGroup = item.source === AssetSource.ClipGroup;
  item.title = item.title;
  item.embargoDate = item.data.accessSettings.effectiveEmbargoDate;
  item.expireDate = item.data.accessSettings.effectiveExpireDate;

  // @ts-ignore - getConformStatus() needs some type signature/parameter tweaking but i don't want to change it right now because i'm under a tight deadline...
  // item.conformStatus = ChannelHelper.getConformStatus(item, undefined);

  // @ts-ignore - type signature says Array<Category> or Array<string>, seems to be an Array<Category> in this context...
  const categories = item.categories as Category[];
  item.categoryString = categories.map((c) => c.name).join(', ');

  // aalt 2023-05-26 cleanup empty tags
  item.tags = item.tags.filter((tag) => !clientUtils.isNullOrWhitespace(tag));
  item.tagString = item.tags.join(', ');
  item.durationString = resolveDurationString(item);
  item.thumbnailSrc = itemUtils.resolveThumbnailSrc(item, '/static/images/thumbnail.png', thumbnails);
  item.publicationDate = item.data.businessMetadata.publicationDate;

  // get seClipId from the externalServiceMetas
  // [{
  //     "meta": null,
  //     "serviceId": "3a1a773e-6dac-41b2-8441-2766ed6b9a83",
  //     "serviceName": "StreamingEngine"
  //   }
  // ]
  if (item && item.data && item.data.externalServiceMetas && item.data.externalServiceMetas.length > 0) {
    const seMetaData = _.find(item.data.externalServiceMetas, (x) => x.serviceName === 'StreamingEngine');
    if (seMetaData && seMetaData.serviceId) {
      item.seClipId = seMetaData.serviceId;
    }
  }

  item.assetSrc = itemUtils.getVideoProxyUrl(item);
  item.isLive = item.sourceFileUrl == null && item.hlsFileUrl != null;

  item.embargoDate = item.data.accessSettings.effectiveEmbargoDate;
  item.isEmbargoed = false;
  item.isExpired = false;
  item.cssClassForEmbargoed = '';
  item.cssClassForExpired = '';
  item.expireDate = item.data.accessSettings.effectiveExpireDate;

  if (item && item.data && item.data.businessMetadata) {
    if (item.embargoDate && new Date(item.embargoDate) > new Date() && appliedExpiredOrEmbargoCss) {
      item.isEmbargoed = true;
      item.cssClassForEmbargoed += 'embargoed';
    }

    if (item.expireDate && new Date(item.expireDate) <= new Date() && appliedExpiredOrEmbargoCss) {
      item.isExpired = true;
      item.cssClassForExpired += 'expired';
    }
  }

  const actions: Action[] = [];
  const submenus: Action[] = [];

  actions.push({
    title: 'Edit',
    className: permissions.has(Permission.EditAsset) ? getActionClassName(item, flags, 'edit') : 'action disabled-icon',
    type: 'edit',
    iconClass: 'icon material-icons',
    iconName: 'mode_edit',
    isDisabled: false,
  });

  // #373214 nested channels only have the edit button as they cannot be linked or clipped, downloaded, or deleted directly
  const isNestedChannel = item.source && item.source.toString() === 'NestedChannel';
  actions.push({
    title: 'Captions',
    className: permissions.has(Permission.EditAsset)
      ? getActionClassName(item, flags, 'uploadExternalWebvttFile')
      : 'action disabled-icon',
    type: 'uploadExternalWebvttFile',
    iconClass: 'icon fa fa-cc',
    iconName: '',
    isDisabled: false,
  });
  actions.push({
    title: 'Set Ad Cue Points',
    className: permissions.has(Permission.AdVariants) ? getActionClassName(item, flags, 'adInsertion') : 'action disabled-icon',
    type: 'adInsertion',
    iconClass: 'icon fa fa-adn',
    iconName: '',
    isDisabled: false,
  });
  actions.push({
    title: 'Create Subclips',
    className: permissions.has(Permission.AdVariants) ? getActionClassName(item, flags, 'clip') : 'action disabled-icon',
    type: 'clip',
    iconClass: 'icon fas ion-scissors',
    iconName: '',
    isDisabled: false,
  });
  actions.push({
    title: 'Link Channel',
    className: permissions.has(Permission.StartWithClip) ? getActionClassName(item, flags, 'link') : 'action disabled-icon',
    type: 'link',
    iconClass: 'icon fa fa-link fa-rotate-90',
    iconName: '',
    isDisabled: false,
  });
  submenus.push({
    title: 'Show  Clip Uses',
    className: 'action',
    type: 'ShowChannelsClipGroups',
    iconClass: 'icon material-icons list',
    iconName: 'list',
    isDisabled: false,
  });
  submenus.push({
    title: 'Clip Timeline',
    className: permissions.has(Permission.ClipTimeline) ? 'action' : 'action disabled-icon',
    type: 'ClipTimeline',
    iconClass: 'icon',
    component: 'Timeline',
    iconName: '',
    isDisabled: false,
  });

  if (permissions.has(Permission.DownloadAsset) && !isClipGroup && !isNestedChannel) {
    submenus.push({
      title: 'Download Asset',
      className: IsIngestComplete(item.data.ingestStatus) && !isClipGroup ? 'action' : 'action disabled-icon',
      type: 'downloadAsset',
      iconClass: 'icon fa fa-download',
      iconName: '',
      isDisabled: false,
    });
  }
  submenus.push({
    title: 'Copy Asset ID',
    className: 'action',
    type: 'CopyAssetId',
    iconClass: 'icon fa fa-clone',
    iconName: '',
    isDisabled: false,
  });
  if (!isClipGroup && !isNestedChannel && permissions.has(Permission.Ingest)) {
    submenus.push({
      title: 'Show Ingest Step Details',
      className: 'action',
      type: 'showIntakeModal',
      iconClass: assetDisplayStatus(item) ? 'icon material-icons bad' : 'icon material-icons',
      iconName: 'dynamic_feed',
      isDisabled: false,
    });
  }
  if (permissions.has(Permission.DeleteAsset) && !isNestedChannel) {
    submenus.push({
      title: 'Delete',
      className: isSuperAdmin ? 'action' : getActionClassName(item, flags, 'deleteAsset'),
      type: 'delete',
      iconClass: 'icon material-icons',
      iconName: 'delete',
      isDisabled: false,
    });
  }

  if (submenus.length > 0) {
    actions.push({
      submenus,
      className: 'show-more-icon',
      iconName: '',
      isDisabled: false,
      component: 'MoreVertIcon',
      title: 'Show More',
      type: 'showMoreIcon',
    });
  }
  item.actions = actions;

  item.isSelectable = isSuperAdmin ? true : resolveIsSelectable(item);

  if (permissions.has(Permission.OUSuperAdmin)) {
    item.variantStatuses = getItemVariantStatuses(item, ouData);
    item.variantStatusInfo = getVariantStatusInfo(item);

    if (channelVariants.length > 0) {
      const markers = itemUtils.checkLowestVariantCodes(item, channelVariants, supportedVariants, true);
      item.clipStatusMarker = markers.status;
      item.differenceVariantCodesWithChannel = markers.variantCodes.join(',');
    } else {
      item.clipStatusMarker = item.variantStatusInfo.hasFailedVariant ? 'danger' : null;
      item.differenceVariantCodesWithChannel = item.variantStatusInfo.failedVariantCodes.join(',');
    }
  }

  item.specialStatus = getSpecialStatusText(item);
  item.segmentStatusInReplay = getSegmentsInReplayStatusText(item);

  return item;
};

const getSpecialStatusText = (item: Item) => {
  if (item && !item.sourceFileUrl && item.hlsFileUrl) {
    return 'Live';
  }
  if (
    (item && !item.sourceFileUrl && !item.hlsFileUrl && !item.parentId) ||
    (item && !item.sourceFileUrl && !item.hlsFileUrl && item.parentId)
  ) {
    return 'Uploading';
  }
  if (
    item &&
    item.sourceFileUrl &&
    item.data &&
    (item.data.ingestStatus === JobStatus.Started || item.data.ingestStatus === JobStatus.Unknown)
  ) {
    return 'Ingesting';
  }
  return null;
};

const getItemVariantStatuses = (item: Item, ouData: OU | null = null) => {
  // console.log('getItemVariantStatuses', { item, ouData });
  const statuses: VariantStatus[] = [];

  if (ouData?.metaData?.ingestVariants != null) {
    const { ingestVariants } = ouData.metaData;
    for (let i = 0; i < ingestVariants.length; i++) {
      statuses.push({ variantCode: ingestVariants[i], status: 'Missing' });
    }
  }

  if (item?.data?.clipHLSData?.variantStatuses != null) {
    const itemVariantStatuses = item.data.clipHLSData.variantStatuses;
    for (const variantCode in itemVariantStatuses) {
      const status = itemVariantStatuses[variantCode];
      const foundIndex = statuses.findIndex((x) => x.variantCode == variantCode);
      // console.log('foundIndex', { foundIndex });
      if (foundIndex > -1) {
        statuses[foundIndex].status = status;
      } else {
        statuses.push({ variantCode, status });
      }
    }
  }

  return statuses;
};

const getVariantStatusInfo = (item: Item): ItemVariantStatusInfo => {
  const failedVariantCodes: string[] = [];

  if (
    item.schemaType != 'Graphics' &&
    item.schemaType != 'Image' &&
    item.source != 'NestedChannel' &&
    item.source != 'ClipGroup' &&
    item.variantStatuses != null &&
    item.variantStatuses.length > 0
  ) {
    for (let i = 0; i < item.variantStatuses.length; i++) {
      const v = item.variantStatuses[i];
      if (v.status == 'Failed') {
        failedVariantCodes.push(v.variantCode);
      }
    }
  }

  return { failedVariantCodes, hasFailedVariant: failedVariantCodes.length > 0 };
};

const getSegmentsInReplayStatusText = (item: Item) => {
  if (item.data && item.data.ingestStatus === JobStatus.Unknown) {
    return 'Live';
  }
  if (item && item.sourceFileUrl && item.data) {
    if (item.data.ingestStatus === JobStatus.Started) {
      return 'Ingesting';
    }
    if (item.data.ingestStatus === JobStatus.Completed) {
      return 'Ingested';
    }
    if (item.data.ingestStatus === JobStatus.Failed) {
      return 'Failed';
    }
  }
  return null;
};
